import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import axiosCancel from 'axios-cancel'
import { api, BASE_API_URL } from '@/services/api'
import { getAuthTokens, updateAuthTokens } from '@/utils/cookies'
import { ServiceAccounts } from '@/services/Accounts'

export const isServerDateString = (value) => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}?((\.\d+)|$)/gi.test(String(value))
}

export const axiosDateTransformer = (res) => {
  try {
    return JSON.parse(res, (key, value) => {
      if (typeof value === 'string' && isServerDateString(value)) {
        const [_dateString, _ms] = value.split('.')
        const _dateNumber = Date.parse(_dateString)

        if (_dateNumber) {
          const ms = (_ms || '000').replace(/\D/g, '')

          return `${_dateString}.${ms}Z`
        }
      }
      return value
    })
  } catch (e) {
    return res
  }
}

const REFRESH_TOKEN_INVALID = 'invalid refresh token'
const REFRESH_TOKEN_EXPIRED = 'refresh token expired'

let isRefreshing = false
let refreshingCall

const instances = [api, axios]

const refreshInstance = axios.create({
  baseURL: BASE_API_URL
})

export default function (app) {
  // axios.defaults.withCredentials = true
  axiosCancel(axios, { debug: false })
  app.axios = axios
  Vue.use(VueAxios, axios)

  // Token
  instances.forEach((instance) => {
    instance.interceptors.request.use((config) => {
      const { token } = getAuthTokens()
      if (token) {
        config.headers = {
          ...config.headers,
          'Authorization': 'Bearer ' + token
        }
      }
      return config
    })
  })

  // Refresh token
  instances.forEach((instance) => {
    instance.interceptors.response.use((res) => res, (error) => {
      const status = error.response ? error.response.status : null
      const { token, refreshToken } = getAuthTokens()

      if (status !== 401 || !(token && refreshToken)) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return refreshingCall.then(() => {
          return instance(error.config)
        })
      }
      isRefreshing = true
      refreshingCall = ServiceAccounts.refreshToken({ token, refreshToken })
        .then(async (res) => {
          const result = await refreshInstance({ ...error.config,
            headers: {
              ...error.config.headers,
              'Authorization': 'Bearer ' + res.data.token
            } })

          isRefreshing = false
          updateAuthTokens(res.data)
          return result
        })
        .catch((err) => {
          const errorStatus = err.response ? err.response.status : null
          const errorMessage = err.response ? err.response.data : null

          isRefreshing = false
          if ([
            errorStatus === 401,
            errorMessage === REFRESH_TOKEN_INVALID,
            errorMessage === REFRESH_TOKEN_EXPIRED
          ].some(Boolean)) {
            updateAuthTokens()
            location.reload()
          }
          return Promise.reject(err)
        })
      return refreshingCall
    })
  })

  // Patch
  instances.forEach((instance) => {
    instance.interceptors.request.use((conf) => {
      if (conf.method === 'patch' && conf.data && typeof conf.data === 'object') {
        conf.data = Object.entries(conf.data)
          .filter(([, value]) => value !== undefined)
          .map(([path, value]) => {
            return { path, value }
          })
      }
      return conf
    })
  })

  // Get
  instances.forEach((instance) => {
    instance.defaults.transformResponse = [...(instance.defaults.transformRequest), axiosDateTransformer]
  })
}
